import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOrg } from "@/context/OrgContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CLIENT_NAV, AMDEN_NAV, CLIENT_BOTTOM_NAV, AMDEN_BOTTOM_NAV, MODULE_ROUTES, moduleEnabled, WORKSPACE_NAV, WORKSPACE_BOTTOM_NAV } from "@/lib/constants";
import { workspaceOn, isWorkspaceOnly, canManageTeam } from "@/lib/workspace";
import { fmtDate } from "@/lib/format";
import { AskAmdenWidget } from "@/components/AskAmdenWidget";
import { ClientSwitcher } from "@/components/ClientSwitcher";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function Icon({ name, ...props }) {
  const C = Icons[name] || Icons.Circle;
  return <C {...props} />;
}

function NavRow({ item, onClick, locked }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? "bg-white/10 text-white"
            : "text-white/55 hover:bg-white/[0.06] hover:text-white/90"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-[#FFC24D] transition-opacity ${
              isActive ? "opacity-100" : "opacity-0"
            }`}
          />
          <Icon name={item.icon} size={18} strokeWidth={1.9} className={locked ? "opacity-45" : ""} />
          <span className={locked ? "opacity-45" : ""}>{item.label}</span>
          {locked && <Icons.Lock size={13} className="ml-auto text-white/35" data-testid={`nav-locked-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />}
        </>
      )}
    </NavLink>
  );
}

function NotificationBell() {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const load = () => api.get("/notifications").then((r) => setNotes(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);
  const unread = notes.filter((n) => !n.read).length;
  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const openNote = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-button"
          className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[#E4E8EF] bg-white text-[#1E2D44] transition-colors hover:border-[#FFC24D]"
        >
          <Icons.Bell size={18} />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#C95555] px-1 text-[10px] font-bold text-white">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-[#E4E8EF] px-4 py-3">
          <span className="font-display text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs font-medium text-[#FFB000] hover:underline" data-testid="mark-all-read">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {notes.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-[#1E2D44]/50">You're all caught up.</div>
          )}
          {notes.map((n) => (
            <button
              key={n.id}
              onClick={() => openNote(n)}
              className={`flex w-full items-start gap-3 border-b border-[#EEF1F6] px-4 py-3 text-left transition-colors hover:bg-[#F4F6F9] ${
                n.read ? "opacity-60" : ""
              }`}
            >
              {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#FFB000]" />}
              {n.read && <span className="mt-1.5 h-2 w-2 shrink-0" />}
              <div>
                <div className="text-sm font-medium text-[#0B1729]">{n.title}</div>
                <div className="text-xs text-[#1E2D44]/60">{n.body}</div>
                <div className="mt-0.5 text-[10px] text-[#1E2D44]/40">{fmtDate(n.created_at)}</div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function WorkspaceBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const tab = new URLSearchParams(location.search).get("tab") || "work";
  return WORKSPACE_BOTTOM_NAV.filter((i) => !i.need || (currentOrg?.module_access || {})[i.need] === true).map((item) => {
    const active = location.pathname === "/workspace" && tab === item.tab;
    return (
      <button
        key={item.tab}
        onClick={() => navigate(`/workspace?tab=${item.tab}`)}
        data-testid={`bottomnav-ws-${item.tab}`}
        className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${active ? "text-[#FFC24D]" : "text-white/50"}`}
      >
        <Icon name={item.icon} size={20} strokeWidth={1.9} />
        {item.label}
      </button>
    );
  });
}

export function AppLayout({ children }) {
  const { user, logout, isAmden, isSuper } = useAuth();
  const { orgs, orgId, clearOrg, currentOrg } = useOrg();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const wsOnly = isWorkspaceOnly(user);
  const wsItems = wsOnly
    ? WORKSPACE_NAV.filter((i) => i.self)
    : workspaceOn(currentOrg) ? WORKSPACE_NAV.filter((i) => (i.self ? !isAmden : canManageTeam(user))) : [];
  const clientNav = wsOnly ? wsItems : [CLIENT_NAV[0], ...wsItems, ...CLIENT_NAV.slice(1)];

  const sidebarContent = (onNav) => (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center px-5">
        <button onClick={() => { navigate(isAmden ? "/portfolio" : "/"); onNav?.(); }}>
          <Logo variant="dark" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto gold-scrollhide px-3 py-2">
        {isAmden && (
          <>
            <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/30">
              Amden
            </div>
            <nav className="space-y-0.5">
              {AMDEN_NAV.filter((item) => !item.superOnly || isSuper).map((item) => (
                <NavRow key={item.to} item={item} onClick={onNav} />
              ))}
            </nav>
          </>
        )}

        {(!isAmden || orgId) && (
          <>
            <div className="mt-4 flex items-center justify-between px-3 pb-1 pt-2">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/30">
                {isAmden ? "Client Workspace" : "Workspace"}
              </span>
            </div>
            {isAmden && currentOrg && (
              <div className="mx-1 mb-2 rounded-lg bg-white/[0.06] px-3 py-2">
                <div className="truncate text-sm font-semibold text-white">{currentOrg.company_name}</div>
                <div className="mt-0.5 flex items-center gap-2">
                  <button
                    onClick={() => { navigate("/clients"); onNav?.(); }}
                    className="text-[11px] text-[#FFC24D] hover:underline"
                    data-testid="switch-client"
                  >
                    Switch client
                  </button>
                  <span className="text-white/20">·</span>
                  <button
                    onClick={() => { clearOrg(); navigate("/portfolio"); onNav?.(); }}
                    className="text-[11px] text-white/45 hover:text-white/70 hover:underline"
                    data-testid="back-to-portfolio"
                  >
                    Portfolio
                  </button>
                </div>
              </div>
            )}
            <nav className="space-y-0.5">
              {clientNav.map((item) => (
                <NavRow
                  key={item.to}
                  item={item}
                  onClick={onNav}
                  locked={!isAmden && MODULE_ROUTES[item.to] && !moduleEnabled(currentOrg, MODULE_ROUTES[item.to])}
                />
              ))}
            </nav>
          </>
        )}
      </div>

      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFB000] text-sm font-bold text-white">
            {user?.name?.[0] || "?"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-white">{user?.name}</div>
            <div className="truncate text-[11px] text-white/45">{user?.role_label || user?.title}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-white/50 transition-colors hover:bg-white/10 hover:text-white"
            title="Sign out"
          >
            <Icons.LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  const bottomNav = isAmden && !orgId ? AMDEN_BOTTOM_NAV : CLIENT_BOTTOM_NAV;

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 z-40 hidden w-64 bg-[#0B1729] lg:block">
        {sidebarContent()}
      </aside>

      {/* Top bar */}
      <header className="fixed left-0 right-0 top-0 z-30 flex h-16 items-center justify-between border-b border-[#E4E8EF] bg-[#F4F6F9]/85 px-4 backdrop-blur-md lg:left-64 lg:px-8">
        <div className="flex items-center gap-3">
          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E4E8EF] bg-white text-[#0B1729] lg:hidden"
                data-testid="mobile-menu-button"
              >
                <Icons.Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-0 bg-[#0B1729] p-0">
              {sidebarContent(() => setMobileOpen(false))}
            </SheetContent>
          </Sheet>

          <div className="lg:hidden">
            <Logo variant="light" showCompass={false} />
          </div>

          {/* Org switcher (Amden) or org name (client) */}
          {isAmden ? (
            <ClientSwitcher />
          ) : (
            currentOrg && (
              <div className="hidden items-center gap-2 sm:flex">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0B1729] text-xs font-bold text-[#FFC24D]">
                  {currentOrg.company_name?.[0]}
                </span>
                <span className="font-display text-sm font-semibold text-[#0B1729]">{currentOrg.company_name}</span>
              </div>
            )
          )}
        </div>

        <div className="flex items-center gap-2.5">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex h-10 items-center gap-2 rounded-full border border-[#E4E8EF] bg-white pl-1 pr-3 transition-colors hover:border-[#FFC24D]"
                data-testid="user-menu-button"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFB000] text-sm font-bold text-[#0B1729]">
                  {user?.name?.[0]}
                </span>
                <span className="hidden text-sm font-medium text-[#0B1729] sm:inline">
                  {user?.name?.split(" ")[0]}
                </span>
                <Icons.ChevronDown size={15} className="text-[#1E2D44]/40" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold">{user?.name}</div>
                <div className="text-xs font-normal text-[#1E2D44]/55">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {wsOnly ? (
                <DropdownMenuItem onClick={() => navigate("/workspace?tab=profile")} data-testid="menu-my-profile">
                  <Icons.IdCard size={15} className="mr-2" /> My profile
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Icons.Settings size={15} className="mr-2" /> Settings
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                <Icons.LogOut size={15} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main */}
      <main className="min-h-screen pt-16 lg:pl-64">
        <div key={location.pathname} className="amden-enter mx-auto max-w-[1400px] px-4 pb-28 pt-6 lg:px-8 lg:pb-12 lg:pt-8">
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex h-16 items-center justify-around border-t border-[#1E2D44] bg-[#0B1729] lg:hidden">
        {wsOnly ? <WorkspaceBottomNav /> : bottomNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`bottomnav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
                isActive ? "text-[#FFC24D]" : "text-white/50"
              }`
            }
          >
            <Icon name={item.icon} size={20} strokeWidth={1.9} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Floating Ask Amden assistant */}
      {!wsOnly && !(!isAmden && !moduleEnabled(currentOrg, "ask_amden")) && <AskAmdenWidget />}
    </div>
  );
}
